import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { FiatWalletAccountState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CreateFiatWalletWithInterstitialModal } from '@sorare/core/src/components/fiatWallet/CreateFiatWalletWithInterstitialModal';
import { InterstitialContextModalMode } from '@sorare/core/src/components/fiatWallet/InterstitialContextModal';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { ClaimButton_taskInterface } from './__generated__/index.graphql.ts';
import { useFiatWalletValidation } from './useFiatWalletValidation.tsx';

type Props = {
  task: ClaimButton_taskInterface;
  isOnboarding?: boolean;
  onClaim?: () => void;
  loading?: boolean;
  className?: string;
};
export const ClaimButton = ({
  isOnboarding,
  onClaim,
  task,
  loading,
  className,
}: Props) => {
  const track = useEvents();
  const { isMobileApp, postMessage } = useIsMobileApp();
  const [showCreateFiatWallet, setShowCreateFiatWallet] = useState(false);
  const { needsKYCValidation, kycUnderReview } = useFiatWalletValidation({
    task,
  });

  return (
    <>
      {!!showCreateFiatWallet && (
        <CreateFiatWalletWithInterstitialModal
          mode={InterstitialContextModalMode.REWARD}
          onDecline={() => setShowCreateFiatWallet(false)}
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          onDismissActivationSuccess={() => setShowCreateFiatWallet(false)}
          onClose={() => setShowCreateFiatWallet(false)}
          canDismissAfterActivation={false}
        />
      )}
      <Button
        color={needsKYCValidation && kycUnderReview ? 'quaternary' : 'primary'}
        size="small"
        loading={!!loading}
        className={className}
        onClick={async () => {
          if (needsKYCValidation) {
            setShowCreateFiatWallet(true);
            return;
          }
          const taskId = task.id;
          track('Click Mission Button', {
            slug: task.taskConfigSlug,
            onboarding: !!isOnboarding,
          });
          await onClaim?.();
          if (isMobileApp) {
            postMessage('taskClaimed', { taskId });
          }
        }}
      >
        {needsKYCValidation && kycUnderReview ? (
          <FormattedMessage defaultMessage="Pending" />
        ) : (
          <FormattedMessage {...glossary.claim} />
        )}
      </Button>
    </>
  );
};

ClaimButton.fragments = {
  taskInterface: gql`
    fragment ClaimButton_taskInterface on TaskInterface {
      id
      taskConfigSlug
      ...useFiatWalletValidation_taskInterface
    }
    ${useFiatWalletValidation.fragments.taskInterface}
  ` as TypedDocumentNode<ClaimButton_taskInterface>,
};
